import { communityTemplateDetailsMessages } from '@/container/community/communityTemplateDetails.messages';
import { TemplateFormDemo } from '@/container/community/template-form-demo';
import { type FormDemoSubmitSource } from '@/container/community/template-form-demo/types';
import { cn } from '@/lib/cn';
import { type DetailedPublicTemplateResponse } from '@/types/global-types';
import { Button } from '@heidi/design-system/button';
import { Skeleton } from '@heidi/design-system/skeleton';
import { TypographyCaptionBold } from '@heidi/design-system/typography';
import { Download, Expand } from 'lucide-react';
import dynamic from 'next/dynamic';
import { FormattedMessage, useIntl } from 'react-intl';

const PdfViewer = dynamic<{ fileUrl: string }>(
  () => import('@/components/modal/form-filling-modals/pdf-viewer'),
  { ssr: false }
);

interface TemplateFormPreviewSectionProps {
  activePdfUrl: string;
  isLoading: boolean;
  onDownloadPdf: () => void;
  onFilledPdfReady: (url: string) => void;
  onOpenFullscreenPreview: () => void;
  onResetDemoPdf: () => void;
  onFormDemoSubmit: (source: FormDemoSubmitSource) => void;
  onFormDemoTrySampleClick: () => void;
  onSignupCtaClick: () => void;
  template: DetailedPublicTemplateResponse | undefined;
  isAuthenticated: boolean;
}

export const TemplateFormPreviewSection = ({
  activePdfUrl,
  isLoading,
  onDownloadPdf,
  onFilledPdfReady,
  onOpenFullscreenPreview,
  onResetDemoPdf,
  onFormDemoSubmit,
  onFormDemoTrySampleClick,
  onSignupCtaClick,
  template,
  isAuthenticated,
}: TemplateFormPreviewSectionProps) => {
  const intl = useIntl();
  const fullscreenLabel = intl.formatMessage(
    communityTemplateDetailsMessages.openFormPreviewFullscreen
  );
  const downloadPdfMessageDescriptor = template?.title
    ? communityTemplateDetailsMessages.downloadPdfForTemplate
    : communityTemplateDetailsMessages.downloadPdfButton;
  const downloadPdfLabel = intl.formatMessage(
    downloadPdfMessageDescriptor,
    template?.title ? { title: template.title } : undefined
  );
  const showTemplateFormDemo = template;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-4 w-full max-w-md" />
        <Skeleton className="h-150 w-full" />
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          'flex items-start gap-2',
          showTemplateFormDemo ? 'justify-between' : 'justify-end'
        )}
      >
        {showTemplateFormDemo && (
          <Button
            className="flex items-center gap-2 text-text-secondary"
            variant="ghost"
            size="xs"
            onClick={onOpenFullscreenPreview}
            aria-label={fullscreenLabel}
            title={fullscreenLabel}
          >
            <Expand size={16} />
            <TypographyCaptionBold>
              <FormattedMessage {...communityTemplateDetailsMessages.expandLabel} />
            </TypographyCaptionBold>
          </Button>
        )}

        <div className="flex flex-wrap items-center justify-end gap-1">
          <Button
            disableAutomaticSvgSizing
            variant="ghost"
            size="xs"
            className="flex items-center gap-2 text-text-secondary"
            onClick={onDownloadPdf}
            aria-label={downloadPdfLabel}
            title={downloadPdfLabel}
          >
            <Download size={16} />
            <FormattedMessage {...communityTemplateDetailsMessages.downloadPdfButton} />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4 @4xl:flex-row @4xl:items-start">
        <div
          className={cn(
            'size-full @4xl:min-w-0',
            showTemplateFormDemo ? '@4xl:grow @4xl:basis-2/3' : '@4xl:flex-1'
          )}
        >
          <PdfViewer fileUrl={activePdfUrl} />
        </div>
        {showTemplateFormDemo && (
          <div className="size-full @4xl:min-w-96 @4xl:max-w-md @4xl:shrink-0 @4xl:basis-1/3">
            <TemplateFormDemo
              template={showTemplateFormDemo}
              onFilledPdfReady={onFilledPdfReady}
              onReset={onResetDemoPdf}
              onSubmitAttempt={onFormDemoSubmit}
              onTrySampleClick={onFormDemoTrySampleClick}
              showSignupCta={!isAuthenticated}
              onSignupCtaClick={onSignupCtaClick}
              isUnauthed={!isAuthenticated}
            />
          </div>
        )}
      </div>
    </>
  );
};
